import { useContext, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'
import { calculateTierProgressionEligibility } from 'loyalty/lib/calculateTierProgressionEligibility'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useCurrentMembershipYear from 'loyalty/hooks/useCurrentMembershipYear'
import moment from 'moment'
import { getTierRank } from 'loyalty/lib/tierUtils'

interface Params {
  offer: App.BedbankOffer | undefined
  rates: Array<App.BedbankRate>
  nights: number
  checkoutDate?: string
  skip?: boolean
}

type BedbankRateTierProgressionResult =
  | {
    showTierProgressionBanner: true
    /** The lowest tier that any rate can unlock */
    lowestUnlockableTier: App.LoyaltyTier
    lowestUnlockableTierName: string
    /** The highest tier that any rate can reach */
    highestUnlockableTier: App.LoyaltyTier
    highestUnlockableTierName: string
    /** True if lowest !== highest (show "fast-track" messaging) */
    hasMultipleTierJumps: boolean
    isEligible: true
    isLoading: false
    membershipYearEndDate: string | undefined
    eligibleRate: App.BedbankRate
  }
  | {
    showTierProgressionBanner: false
    lowestUnlockableTier: undefined
    lowestUnlockableTierName: undefined
    highestUnlockableTier: undefined
    highestUnlockableTierName: undefined
    hasMultipleTierJumps: false
    isEligible: boolean
    isLoading: boolean
    membershipYearEndDate: string | undefined
    eligibleRate: undefined
  }

function useBedbankRateTierProgression(params: Params): BedbankRateTierProgressionResult {
  const { offer, rates, nights, checkoutDate, skip = false } = params

  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const loyaltyAccountLoading = useAppSelector(
    state => state.loyalty.account.fetching || state.loyalty.account.initial,
  )
  const pendingPoints = useAppSelector(state => state.loyalty.accountPendingPoints.data)
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)
  const membershipYear = useCurrentMembershipYear()
  const membershipYearEndDate = membershipYear?.end.toISOString()

  const calculationRequests = useMemo(() => {
    if (skip || loyaltyAccountLoading || !offer || rates.length === 0 || nights <= 0) {
      return []
    }

    return rates.map(rate =>
      generateLoyaltyPointsCalculatorBedbankOptions(rate, {
        price: rate.totals.total,
        memberPrice: rate.totals.memberTotal ?? 0,
        propertyFees: rate.totals.propertyFees ?? 0,
        propertyId: offer.property.id,
        offerId: offer.id,
        propertyCountryCode: offer.property.address.countryCode,
        propertyState: offer.property.address.stateProvinceCode,
        currentRegionCode,
        nights,
      }),
    )
  }, [skip, loyaltyAccountLoading, offer, rates, currentRegionCode, nights])

  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: skip || calculationRequests.length === 0,
    useBatch: true,
  })

  const result = useMemo((): BedbankRateTierProgressionResult => {
    const anyLoading = pointsCalculations.some(calc => calc?.initial || calc?.fetching)
    const isLoading = loyaltyAccountLoading || anyLoading || !tierThresholds

    if (skip || isLoading || !loyaltyAccount || rates.length === 0 || !tierThresholds) {
      return {
        showTierProgressionBanner: false,
        lowestUnlockableTier: undefined,
        lowestUnlockableTierName: undefined,
        highestUnlockableTier: undefined,
        highestUnlockableTierName: undefined,
        hasMultipleTierJumps: false,
        isEligible: false,
        isLoading: !skip && isLoading,
        membershipYearEndDate,
        eligibleRate: undefined,
      }
    }

    const pointsEarnDate = checkoutDate
      ? moment(checkoutDate).add(1, 'day').format('YYYY-MM-DD')
      : undefined

    // Track across ALL rates:
    // - globalLowestTier: the lowest tier any rate can unlock
    // - globalHighestTier: the highest tier any rate can reach
    let globalLowestTier: App.LoyaltyTier | undefined
    let globalHighestTier: App.LoyaltyTier | undefined
    let bestRate: App.BedbankRate | undefined

    for (let i = 0; i < rates.length; i++) {
      const rate = rates[i]
      const calculation = pointsCalculations[i]

      if (!calculation?.data) continue

      const baseStatusCredits = calculation.data.statusCredits ?? 0
      const bonusStatusCredits = calculation.data.bonusStatusCredits ?? 0
      const totalStatusCredits = baseStatusCredits + bonusStatusCredits

      const eligibility = calculateTierProgressionEligibility({
        currentTier: loyaltyAccount.tier,
        currentStatusCredits: loyaltyAccount.statusCredits ?? 0,
        currentNights: loyaltyAccount.numberOfNights ?? 0,
        statusCredits: totalStatusCredits,
        nights,
        pointsEarnDate,
        membershipYearEndDate,
        pendingPoints: pendingPoints ?? [],
        tierThresholds,
      })

      if (eligibility.isEligible) {
        // Track the LOWEST unlockable tier across all rates
        const currentLowestRank = getTierRank(globalLowestTier)
        const newLowestRank = getTierRank(eligibility.lowestUnlockableTier)
        if (currentLowestRank === -1 || newLowestRank < currentLowestRank) {
          globalLowestTier = eligibility.lowestUnlockableTier
        }

        // Track the HIGHEST unlockable tier across all rates
        const currentHighestRank = getTierRank(globalHighestTier)
        const newHighestRank = getTierRank(eligibility.highestUnlockableTier)
        if (newHighestRank > currentHighestRank) {
          globalHighestTier = eligibility.highestUnlockableTier
          bestRate = rate
        }
      }
    }

    // Return combined result if we found any eligible rates
    if (globalLowestTier && globalHighestTier && bestRate) {
      const hasMultipleTierJumps = globalLowestTier !== globalHighestTier

      return {
        showTierProgressionBanner: true,
        lowestUnlockableTier: globalLowestTier,
        lowestUnlockableTierName: getLoyaltyTierDisplayName(globalLowestTier),
        highestUnlockableTier: globalHighestTier,
        highestUnlockableTierName: getLoyaltyTierDisplayName(globalHighestTier),
        hasMultipleTierJumps,
        isEligible: true,
        isLoading: false,
        membershipYearEndDate,
        eligibleRate: bestRate,
      }
    }

    return {
      showTierProgressionBanner: false,
      lowestUnlockableTier: undefined,
      lowestUnlockableTierName: undefined,
      highestUnlockableTier: undefined,
      highestUnlockableTierName: undefined,
      hasMultipleTierJumps: false,
      isEligible: false,
      isLoading: false,
      membershipYearEndDate,
      eligibleRate: undefined,
    }
  }, [skip, loyaltyAccountLoading, loyaltyAccount, rates, pointsCalculations, nights, checkoutDate, pendingPoints, tierThresholds, membershipYearEndDate])

  return result
}

export default useBedbankRateTierProgression
